/**
 * Self-heal for a tab that outlived a deploy.
 *
 * Route chunks are hash-named, so a browser holding the previous build asks for
 * filenames the new build does not have. The lazy import then fails with
 * "Failed to fetch dynamically imported module" — a dead end for something a
 * reload fixes.
 *
 * The failure surfaces in two places, so both call this:
 *   - ``vite:preloadError`` when the module preload 404s.
 *   - the ErrorBoundary when it comes through React.lazy instead.
 */
const RELOAD_KEY = 'stale-chunk-reload'

/** Message fragments browsers use for a missing/unparseable module. */
const STALE_CHUNK_SIGNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
]

export function isStaleChunkError(reason: unknown): boolean {
  const text = String((reason as { message?: string } | null)?.message ?? reason ?? '')
  return STALE_CHUNK_SIGNS.some((sign) => text.includes(sign))
}

/**
 * Reload once for a stale-chunk error. Returns true when a reload was started,
 * so the caller can skip rendering an error the user will never read.
 *
 * Reloading is capped at once per session: if the chunk is still missing after
 * a fresh index.html, something else is wrong and looping would hide it.
 */
export function reloadOnceForStaleChunk(reason: unknown): boolean {
  if (!isStaleChunkError(reason)) return false
  if (sessionStorage.getItem(RELOAD_KEY)) return false
  sessionStorage.setItem(RELOAD_KEY, '1')
  window.location.reload()
  return true
}

/**
 * Re-arm after the page has run healthily for a while, so a tab left open
 * across two deploys heals twice.
 *
 * Deliberately NOT on the ``load`` event: that fires during the very reload
 * this module triggers, which would clear the cap and loop forever.
 */
export function rearmStaleChunkReload(afterMs = 10_000): void {
  window.setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), afterMs)
}
